import { supabase } from './supabaseClient';

export interface WellnessDimension {
  id: string;
  facilityId: string;
  name: string;
  icon: string;
  color: string;
  description?: string;
  metricKey?: string; // CareDaily category root for this dimension (e.g. "dow.physical").
  valueKey?: string | null; // Optional reading sub-field. Null = avg ?? value.
  goiconDimensionId?: number;
  goiconDimensionName?: string;
  categoryId?: string | null;
  sortOrder: number;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export class DimensionService {
  /**
   * Get all wellness dimensions for a facility
   */
  static async getDimensions(facilityId: string): Promise<WellnessDimension[]> {
    console.log('🔍 Fetching wellness dimensions for facility:', facilityId);

    const { data, error } = await supabase
      .from('wellness_dimensions')
      .select('*')
      .eq('facility_id', facilityId)
      .order('sort_order', { ascending: true });

    if (error) {
      console.error('❌ Error fetching dimensions:', error);
      throw error;
    }

    console.log('✅ Retrieved dimensions:', data);
    return (data || []).map(this.mapDbToDimension);
  }

  /**
   * Create a new wellness dimension
   */
  static async createDimension(
    facilityId: string,
    dimension: Omit<WellnessDimension, 'id' | 'facilityId' | 'createdAt' | 'updatedAt'>
  ): Promise<WellnessDimension> {
    console.log('📝 Creating wellness dimension:', dimension);

    const { data, error } = await supabase
      .from('wellness_dimensions')
      .insert([{
        facility_id: facilityId,
        name: dimension.name,
        icon: dimension.icon,
        color: dimension.color,
        description: dimension.description,
        metric_key: dimension.metricKey,
        value_key: dimension.valueKey || null,
        goicon_dimension_id: dimension.goiconDimensionId,
        goicon_dimension_name: dimension.goiconDimensionName,
        category_id: dimension.categoryId,
        sort_order: dimension.sortOrder,
        is_active: dimension.isActive
      }])
      .select()
      .single();

    if (error) {
      console.error('❌ Error creating dimension:', error);
      throw error;
    }

    console.log('✅ Dimension created successfully');
    return this.mapDbToDimension(data);
  }

  /**
   * Update an existing wellness dimension
   */
  static async updateDimension(
    id: string,
    updates: Partial<Omit<WellnessDimension, 'id' | 'facilityId' | 'createdAt' | 'updatedAt'>>
  ): Promise<WellnessDimension> {
    console.log('📝 Updating wellness dimension:', id, updates);

    const updateData: any = {};
    if (updates.name !== undefined) updateData.name = updates.name;
    if (updates.icon !== undefined) updateData.icon = updates.icon;
    if (updates.color !== undefined) updateData.color = updates.color;
    if (updates.description !== undefined) updateData.description = updates.description;
    if (updates.metricKey !== undefined) updateData.metric_key = updates.metricKey;
    if (updates.valueKey !== undefined) updateData.value_key = updates.valueKey || null;
    if (updates.goiconDimensionId !== undefined) updateData.goicon_dimension_id = updates.goiconDimensionId;
    if (updates.goiconDimensionName !== undefined) updateData.goicon_dimension_name = updates.goiconDimensionName;
    if (updates.categoryId !== undefined) updateData.category_id = updates.categoryId;
    if (updates.sortOrder !== undefined) updateData.sort_order = updates.sortOrder;
    if (updates.isActive !== undefined) updateData.is_active = updates.isActive;

    const { data, error } = await supabase
      .from('wellness_dimensions')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('❌ Error updating dimension:', error);
      throw error;
    }

    console.log('✅ Dimension updated successfully');
    return this.mapDbToDimension(data);
  }

  /**
   * Delete a wellness dimension
   */
  static async deleteDimension(id: string): Promise<void> {
    console.log('🗑️ Deleting wellness dimension:', id);

    const { error } = await supabase
      .from('wellness_dimensions')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('❌ Error deleting dimension:', error);
      throw error;
    }

    console.log('✅ Dimension deleted successfully');
  }

  /**
   * Get the next sort order value for a facility
   */
  static async getNextSortOrder(facilityId: string): Promise<number> {
    const { data, error } = await supabase
      .from('wellness_dimensions')
      .select('sort_order')
      .eq('facility_id', facilityId)
      .order('sort_order', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) {
      console.error('❌ Error getting max sort order:', error);
      return 1;
    }

    return (data?.sort_order || 0) + 1;
  }

  /**
   * Map database row to WellnessDimension
   */
  private static mapDbToDimension(row: any): WellnessDimension {
    return {
      id: row.id,
      facilityId: row.facility_id,
      name: row.name,
      icon: row.icon || '📊',
      color: row.color,
      description: row.description || '',
      metricKey: row.metric_key,
      valueKey: row.value_key || null,
      goiconDimensionId: row.goicon_dimension_id ?? undefined,
      goiconDimensionName: row.goicon_dimension_name ?? undefined,
      categoryId: row.category_id,
      sortOrder: row.sort_order || 0,
      isActive: row.is_active ?? true,
      createdAt: row.created_at,
      updatedAt: row.updated_at
    };
  }
}








